import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import { DataGrid } from '../../common/datagrid/ResponsiveDataGrid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import ProcessMasterDialog from './ProcessMasterDialog';
import * as manufacturingApi from '../../../lib/api/manufacturing';

const ProcessMasterList = () => {
  // 상태 관리
  const [loading, setLoading] = useState(true);
  const [processes, setProcesses] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ===========================================
  // 데이터 로드
  // ===========================================
  const fetchProcesses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await manufacturingApi.getProcessMasters({ page: 1, size: 100 });
      console.log('공정 마스터 목록 API 응답:', response.data);
      
      if (response.data.success) {
        const list = response.data.data?.content || response.data.data || [];
        // 프론트엔드 형식으로 변환
        const formattedList = list.map(item => ({
          id: item.processUuid,
          processUuid: item.processUuid,
          processCode: item.processCd,
          processName: item.processNm,
          processType: item.processTyp || '-',
          description: item.processDesc || '-',
          standardTime: item.standardTime || 0,
          useYn: item.useYn,
          // 담당 팀과 주·부 담당자. 수정 팝업이 다시 채우려면 목록에서 들고 있어야 한다.
          dptUuid: item.dptUuid || '',
          dptNm: item.dptNm || '',
          defaultWorkerUuid: item.defaultWorkerUuid || '',
          defaultWorkerNm: item.defaultWorkerNm || '',
          subWorkerUuid: item.subWorkerUuid || '',
          subWorkerNm: item.subWorkerNm || '',
          createdBy: item.crtUsrNm || '-',
          createdDate: item.crtDt ? item.crtDt.substring(0, 10) : '-',
        }));
        setProcesses(formattedList);
      } else {
        showSnackbar(response.data.message || '공정 마스터 목록 조회 실패', 'error');
      }
    } catch (error) {
      console.error('공정 마스터 목록 조회 에러:', error);
      showSnackbar('공정 마스터 목록을 불러오는데 실패했습니다.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProcesses();
  }, [fetchProcesses]);

  // ===========================================
  // 스낵바
  // ===========================================
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // ===========================================
  // 이벤트 핸들러
  // ===========================================
  const handleAddNew = () => {
    setSelectedProcess(null);
    setOpenDialog(true);
  };

  const handleEdit = async (process) => {
    try {
      // 상세 정보 조회
      const response = await manufacturingApi.getProcessMaster(process.processUuid);
      if (response.data.success) {
        setSelectedProcess(response.data.data);
        setOpenDialog(true);
      }
    } catch (error) {
      console.error('공정 마스터 상세 조회 에러:', error);
      showSnackbar('공정 정보를 불러오는데 실패했습니다.', 'error');
    }
  };

  const handleDelete = async (process) => {
    if (!window.confirm(`"${process.processName}" 공정을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const response = await manufacturingApi.deleteProcessMaster(process.processUuid);
      if (response.data.success) {
        showSnackbar('공정이 삭제되었습니다.', 'success');
        fetchProcesses();
      } else {
        showSnackbar(response.data.message || '삭제 실패', 'error');
      }
    } catch (error) {
      console.error('공정 마스터 삭제 에러:', error);
      showSnackbar('공정 삭제에 실패했습니다.', 'error');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedProcess(null);
  };

  const handleSave = async (processData) => {
    try {
      // API 요청 데이터 형식으로 변환
      const requestData = {
        processCd: processData.processCode,
        processNm: processData.processName,
        processTyp: processData.processType || 'STANDARD',
        processDesc: processData.description,
        standardTime: processData.standardTime || 0,
        useYn: processData.useYn || 'Y',
        dptUuid: processData.dptUuid || '',
        defaultWorkerUuid: processData.defaultWorkerUuid || '',
        subWorkerUuid: processData.subWorkerUuid || '',
      };

      let response;
      if (selectedProcess?.processUuid) {
        // 수정
        response = await manufacturingApi.updateProcessMaster(selectedProcess.processUuid, requestData);
      } else {
        // 신규 생성
        response = await manufacturingApi.createProcessMaster(requestData);
      }

      if (response.data.success) {
        showSnackbar(
          selectedProcess?.processUuid ? '공정이 수정되었습니다.' : '공정이 생성되었습니다.',
          'success'
        );
        handleCloseDialog();
        fetchProcesses();
      } else {
        showSnackbar(response.data.message || '저장 실패', 'error');
      }
    } catch (error) {
      console.error('공정 마스터 저장 에러:', error);
      showSnackbar('공정 저장에 실패했습니다.', 'error');
    }
  };

  // ===========================================
  // 공정 유형 라벨
  // ===========================================
  const getProcessTypeLabel = (type) => {
    // 공통코드(MANU/PROCESS_TYP)에 등록된 값만 쓴다. OUTSOURCE 와 QC 는 어디에도 없던 값이라
    // 뺐다 — 검사 공정을 뜻하는 값은 INSPECTION 이다.
    const types = {
      'DESIGN': '설계',
      'STANDARD': '표준',
      'CUSTOM': '커스텀',
      // '검사' 가 아니라 '완품검사' 다. 이 유형은 공정 중간검사가 아니라 계획 전체를
      // 합격/불합격으로 판정하는 공정이고, 불합격이면 전량 폐기된다.
      'INSPECTION': '완품검사',
      'ASSEMBLY': '조립',
    };
    return types[type] || type;
  };

  // ===========================================
  // 컬럼 정의
  // ===========================================
  const columns = useMemo(
    () => [
      {
        field: 'processCode',
        headerName: '공정 코드',
        width: 150,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontWeight: 600,
              color: '#1976D2',
            }}
          >
            {params.value || '-'}
          </Typography>
        ),
      },
      {
        field: 'processName',
        headerName: '공정명',
        width: 200,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => (
          <Typography variant="body2" fontWeight={600}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'processType',
        headerName: '공정 유형',
        width: 120,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Chip
            label={getProcessTypeLabel(params.value)}
            size="small"
            color={
              params.value === 'DESIGN' ? 'info' :
              params.value === 'STANDARD' ? 'primary' :
              params.value === 'CUSTOM' ? 'secondary' :
              params.value === 'INSPECTION' ? 'warning' : 'default'
            }
            variant="outlined"
          />
        ),
      },
      {
        field: 'description',
        headerName: '공정 설명',
        minWidth: 300,
        flex: 1,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'standardTime',
        headerName: '표준 시간 (분)',
        width: 120,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Typography variant="body2">
            {params.value ? `${params.value}분` : '-'}
          </Typography>
        ),
      },
      {
        field: 'useYn',
        headerName: '사용',
        width: 80,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Chip
            label={params.value === 'Y' ? '사용' : '미사용'}
            size="small"
            color={params.value === 'Y' ? 'success' : 'default'}
            variant={params.value === 'Y' ? 'filled' : 'outlined'}
          />
        ),
      },
      {
        field: 'createdBy',
        headerName: '작성자',
        width: 100,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'createdDate',
        headerName: '작성일',
        width: 120,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'actions',
        headerName: '작업',
        width: 120,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleEdit(params.row)}
              title="수정"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(params.row)}
              title="삭제"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ],
    []
  );

  // ===========================================
  // 렌더링
  // ===========================================
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 1.5, sm: 0 },
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6">공정 마스터 관리</Typography>
          <Typography variant="body2" color="text.secondary">
            생산 공정의 기본 정보를 관리합니다
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchProcesses}
            disabled={loading}
          >
            새로고침
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNew}>
            공정 추가
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          공정 마스터는 워크플로우를 구성하는 기본 단위입니다. 공정을 먼저 등록한 후 워크플로우에서 조합하여 사용할 수 있습니다.
        </Typography>
      </Alert>

      <Paper elevation={1}>
        <Box sx={{ height: 600, width: '100%' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={processes}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10 },
                },
                sorting: {
                  sortModel: [{ field: 'processCode', sort: 'asc' }],
                },
              }}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              localeText={{
                noRowsLabel: '등록된 공정이 없습니다.',
              }}
              sx={{
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
              }}
            />
          )}
        </Box>
      </Paper>

      <ProcessMasterDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onSave={handleSave}
        selectedProcess={selectedProcess}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProcessMasterList;
